import { Box } from "arkh";
import React, { useCallback, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { WidgetFileStatic_widgetFile$key } from "./__generated__/WidgetFileStatic_widgetFile.graphql";
import { PageIndicator } from "./PageIndicator";

import { WidgetFilePageProcessing, WidgetFilePage } from "./WidgetFilePage";
import { TouchPageButtons } from "../WidgetNote/TouchPageButtons";
import { useInterval } from "../../useInterval";
function noOp() {}
export function WidgetFileStatic(props: {
  widget: WidgetFileStatic_widgetFile$key;
  pageId?: string;
  isFocusMode?: boolean;
  isTouch?: boolean;
  onPagePress?: (fileId: string, pageId: string) => void;
}) {
  const widget = useFragment(
    graphql`
      fragment WidgetFileStatic_widgetFile on WidgetFile {
        id
        title
        slideshowDuration
        files {
          pages {
            id
            ...WidgetFilePage_page
          }
          file {
            id
            status
            ...WidgetFilePageProcessing_file
            name
          }
        }
      }
    `,
    props.widget
  );

  type ListItemType =
    | {
        fileId: string;
        type: "PAGE";
        page: (typeof widget.files)[number]["pages"][number];
      }
    | {
        fileId: string;
        type: "PROCESSING";
        file: (typeof widget.files)[number]["file"];
      };

  const listData = useMemo(
    () =>
      widget.files.flatMap<ListItemType>((f) =>
        f.file.status === "READY"
          ? f.pages.map((p) => ({
              fileId: f.file.id,
              page: p,
              type: "PAGE" as const,
            }))
          : [{ type: "PROCESSING" as const, file: f.file, fileId: f.file.id }]
      ),
    [widget]
  );

  const [_index, setIndex] = React.useState(0);
  const [dismissedPageId, setDismissedPageId] = useState(false);

  const pageId = dismissedPageId ? undefined : props.pageId;

  const { isTouch, isFocusMode } = props;
  const index = useMemo(() => {
    if (isFocusMode && !isTouch) {
      if (pageId) {
        const found = listData.findIndex(
          (page) => page.type === "PAGE" && page.page.id === pageId
        );
        if (found >= 0) {
          return found;
        }
      }
      return 0;
    }
    return _index;
  }, [_index, listData, isFocusMode, pageId, isTouch]);

  useInterval(
    () => {
      setIndex((prevIndex) =>
        listData.length === 0 ? 0 : (prevIndex + 1) % listData.length
      );
    },
    props.isFocusMode ? null : (widget.slideshowDuration ?? 15) * 1000
  );

  const currentPage = listData[index];
  const onPaginate = useCallback(
    (page: -1 | 1) => {
      setDismissedPageId(true);
      setIndex((prevIndex) => {
        const next = (prevIndex + page) % listData.length;
        if (next < 0) {
          return listData.length - 1;
        }
        return next;
      });
    },
    [listData]
  );
  const _onPagePress = props.onPagePress;
  const onPagePress = useCallback(
    (pageId: string) => {
      if (currentPage?.type === "PAGE") {
        _onPagePress?.(currentPage.fileId, pageId);
      }
    },
    [currentPage, _onPagePress]
  );

  if (!currentPage) {
    return null;
  }
  return (
    <>
      <Box
        color="surface-backgroundControl"
        cornerRadius="medium"
        style={{
          flex: 1,
          overflow: "hidden",
        }}
      >
        {currentPage.type === "PROCESSING" ? (
          <WidgetFilePageProcessing file={currentPage.file} />
        ) : (
          <WidgetFilePage
            fileId={currentPage.fileId}
            onPress={props.isTouch ? onPagePress : noOp}
            page={currentPage.page}
          />
        )}
      </Box>
      {props.isTouch ? (
        <>
          <Box
            style={{
              position: "absolute",

              bottom: 0,
              right: 0,
            }}
            spaceHorizontal="small"
            spaceRight="large"
          >
            <Box spaceRight="large">
              <TouchPageButtons onPaginate={onPaginate} />
            </Box>
          </Box>
        </>
      ) : null}
      {listData.length > 1 ? (
        <PageIndicator
          currentPage={index + 1}
          numberOfItems={listData.length}
        />
      ) : null}
    </>
  );
}
